import React, { useState, useEffect, useRef } from 'react';
import { apiGet, apiPost } from '../../../../lib/api.js';
import {
  ShieldCheck,
  Loader2,
  AlertTriangle,
  RefreshCw,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { ShakenBadge, TrustRow, REPUTATION_ROWS, repState } from './TrustIndicators.jsx';
import StepDone from './StepDone.jsx';

// Post-purchase trust-chain progress panel. Polls GET /phones/:sid/trust-status
// every 2s (up to 60s) until provisioningStatus is terminal, rendering each
// step as spinner → check/X. On `partial`, offers a Retry that re-fires the
// chain. When trusted, shows the completion actions.
/**
 * Step 5 (single) — polls STIR/SHAKEN + spam-reputation progress.
 * @param {Object} props - purchased record plus onGoSettings/onGoTemplates/onBuyAnother/onSettled callbacks.
 */
export default function StepProvisioning({ purchased, onGoSettings, onGoTemplates, onBuyAnother, onSettled }) {
  const sid = purchased.twilioSid || purchased.number;
  // Simulated / already-trusted purchases render all-green immediately — no
  // polling delay, no spinners.
  const alreadyTrusted = purchased.simulated === true || purchased.provisioningStatus === 'trusted';
  const [status, setStatus] = useState(() => ({
    provisioningStatus: alreadyTrusted ? 'trusted' : (purchased.provisioningStatus || 'provisioning'),
    shakenAttestation: alreadyTrusted ? 'A' : (purchased.shakenAttestation || 'A'),
    messagingServiceAttached: alreadyTrusted ? true : purchased.messagingServiceAttached !== false,
    reputation: alreadyTrusted
      ? Object.fromEntries(REPUTATION_ROWS.map((row) => [row.key, { status: 'clean' }]))
      : {},
  }));
  const [timedOut, setTimedOut] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const startedRef = useRef(Date.now());

  const terminal = status.provisioningStatus === 'trusted'
    || status.provisioningStatus === 'partial'
    || status.provisioningStatus === 'simulated'
    || status.provisioningStatus === 'failed';

  useEffect(() => {
    if (!sid || terminal) return undefined;
    let cancelled = false;
    const iv = setInterval(async () => {
      if (Date.now() - startedRef.current > 60000) {
        if (!cancelled) { setTimedOut(true); clearInterval(iv); }
        return;
      }
      try {
        const r = await apiGet(`/phones/${encodeURIComponent(sid)}/trust-status`);
        if (cancelled) return;
        if (r?.trustStatus) {
          setStatus(r.trustStatus);
          if (['trusted', 'partial', 'simulated', 'failed'].includes(r.trustStatus.provisioningStatus)) {
            clearInterval(iv);
            onSettled?.();
          }
        }
      } catch {
        // transient — keep polling until the 60s cap
      }
    }, 2000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [sid, terminal, onSettled]);

  async function retry() {
    setRetrying(true);
    try {
      const r = await apiPost(`/phones/${encodeURIComponent(sid)}/retry-trust-chain`, {});
      setStatus((s) => ({
        ...s,
        provisioningStatus: r.provisioningStatus || s.provisioningStatus,
        shakenAttestation: r.shakenAttestation || s.shakenAttestation,
      }));
      startedRef.current = Date.now();
      setTimedOut(false);
      onSettled?.();
      toast.success('Trust chain retried.');
    } catch (e) {
      toast.error(e.response?.data?.error || 'Retry failed.');
    } finally {
      setRetrying(false);
    }
  }

  const isPartial = status.provisioningStatus === 'partial';

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-full bg-civix-purple/20 inline-flex items-center justify-center">
          <ShieldCheck className="w-5 h-5 text-civix-purple" />
        </div>
        <div>
          <div className="font-display font-bold text-lg">Registering {purchased.number} for trusted calling</div>
          <div className="text-xs text-white/55">STIR/SHAKEN + spam-protection registration — usually under a minute.</div>
        </div>
      </div>

      <div className="p-4 rounded-md border border-civix-border/50 bg-black/20">
        <ul>
          <TrustRow label="Purchase number" state="done" />
          <TrustRow label="Attach to Messaging Service" state={status.messagingServiceAttached ? 'done' : 'pending'} />
          <TrustRow label="Register STIR/SHAKEN attestation" state={status.shakenAttestation === 'A' ? 'done' : 'pending'} />
          {REPUTATION_ROWS.map((row) => (
            <TrustRow key={row.key} label={row.label} state={repState(status.reputation?.[row.key]?.status)} />
          ))}
        </ul>
      </div>

      <div className="flex items-center gap-3">
        <span className="text-xs text-white/55">Final attestation:</span>
        <ShakenBadge level={status.shakenAttestation} />
      </div>

      {isPartial && (
        <div role="alert" className="p-3 rounded-md border border-amber-500/50 bg-amber-500/10 text-sm text-amber-200 flex items-center justify-between gap-3">
          <span className="inline-flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            Number is live and A-attested, but some spam-registry submissions didn't complete.
          </span>
          <button onClick={retry} disabled={retrying} className="btn-ghost inline-flex items-center gap-1 text-amber-100 disabled:opacity-50">
            {retrying ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Retry
          </button>
        </div>
      )}

      {timedOut && !terminal && (
        <div role="alert" className="p-3 rounded-md border border-white/20 bg-white/5 text-sm text-white/70">
          Still finishing in the background. Your number is live — reputation registration will complete shortly.
        </div>
      )}

      {(terminal || timedOut) && (
        <StepDone purchased={purchased} onGoSettings={onGoSettings} onGoTemplates={onGoTemplates} onBuyAnother={onBuyAnother} />
      )}
    </div>
  );
}
